import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { store } from './store/store';
import { theme } from './theme';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CategoriesList from './pages/CategoriesList';
import SubcategoriesList from './pages/SubcategoriesList';
import SubcategoryProductsList from './pages/SubcategoryProductsList';
import ProductsList from './pages/ProductsList';
import AddProduct from './pages/AddProduct';
import OrdersList from './pages/OrdersList';
import OrderDetails from './pages/OrderDetails';
import UsersList from './pages/UsersList';
import GroupsList from './pages/GroupsList';
import Settings from './pages/Settings';
import ChangePassword from './pages/ChangePassword';
import BannersList from './pages/BannersList';
import ProductMaster from './pages/ProductMaster';
import { useAppDispatch } from './hooks/redux';
import { initializeAuth } from './store/slices/authSlice';
import Faqs from './pages/Faqs';
import AboutReward from './pages/AboutReward';
import TermsAndConditions from './pages/TermsandCondition';
import ServerDown from './pages/ServerDown';
import NotFound from './pages/PagenotFound';
import GroupAgentsList from './pages/GroupAgentsList';
import ManagePermissions from './pages/ManagePermissions';

// Component to initialize auth state
const AuthInitializer: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  return null;
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthInitializer />
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={<MainLayout><Dashboard /></MainLayout>} />
            
            {/* Categories Management */}
            <Route path="/categories" element={<MainLayout><CategoriesList /></MainLayout>} />
            <Route path="/subcategories/:categoryId" element={<MainLayout><SubcategoriesList /></MainLayout>} />
            <Route path="/subcategories/:categoryId/products/:subcategoryId" element={<MainLayout><SubcategoryProductsList /></MainLayout>} />
            
            {/* Products Management */}
            <Route path="/products" element={<MainLayout><ProductsList /></MainLayout>} />
            <Route path="/add-product" element={<MainLayout><AddProduct /></MainLayout>} />
            <Route path="/add-product/:id" element={<MainLayout><AddProduct /></MainLayout>} />
            
            {/* Orders Management */}
            <Route path="/orders" element={<MainLayout><OrdersList /></MainLayout>} />
            <Route path="/order/:orderId" element={<MainLayout><OrderDetails /></MainLayout>} />
            
            {/* Users/Agents Management */}
            <Route path="/agent" element={<MainLayout><UsersList /></MainLayout>} />
            
            {/* Groups Management */}
            <Route path="/groups" element={<MainLayout><GroupsList /></MainLayout>} />
            <Route path="/group-agents/:groupId" element={<MainLayout><GroupAgentsList /></MainLayout>} />
            
            {/* Settings */}
            <Route path="/settings" element={<MainLayout><Settings /></MainLayout>} />
            {/* <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicy /></MainLayout>} /> */}
            <Route path="/terms-and-conditions" element={<MainLayout><TermsAndConditions /></MainLayout>} />
            <Route path="/faqs" element={<MainLayout><Faqs /></MainLayout>} />
            <Route path="/about-reward" element={<MainLayout><AboutReward /></MainLayout>} />
            
            {/* Change Password */}
            <Route path="/changepassword" element={<MainLayout><ChangePassword /></MainLayout>} />
            <Route path="/banners" element={<MainLayout><BannersList /></MainLayout>} />
            <Route path="/product-master" element={<MainLayout><ProductMaster /></MainLayout>} />
            <Route path="/manage-permissions" element={<MainLayout><ManagePermissions /></MainLayout>} />
            <Route path="/serverdown" element={<ServerDown />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
        <ToastContainer />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
